import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const api = axios.create({
  baseURL: `${BACKEND_URL}/api`,
  withCredentials: true,  // Send httpOnly cookies on every request
});

api.interceptors.request.use((config) => {
  // Cookie-based auth is automatic (httpOnly cookie sent by browser).
  // Keep Authorization header as fallback for legacy sessions still in localStorage.
  const token = localStorage.getItem("token");
  if (token) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

/**
 * IDK1-A — a chave de idempotência do lado do cliente.
 *
 * ⚠️ Porque NÃO está no interceptor. Injectar `Idempotency-Key` em todos os POST
 * seria guarda larga: cobre tudo e não responde por nada. No dia em que outra
 * rota passe a exigi-la, o comportamento mudaria em silêncio e ninguém saberia
 * de onde veio a chave. Aqui é explícito no sítio que a precisa.
 *
 * O DOMÍNIO é o que o servidor anuncia na recusa `428`: 8 a 255 caracteres ASCII
 * imprimíveis, sem espaços. `randomUUID` dá 36 hexadecimais com hífens, que cabe
 * com folga; o ramo de recurso existe para navegadores sem `crypto.randomUUID`
 * (contexto não-seguro), e é construído para ficar no mesmo domínio.
 */
export function chaveDeOperacao() {
  const uuid = globalThis.crypto?.randomUUID?.();
  if (uuid) return uuid;
  return `k${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 12)}`;
}

/** Config de pedido que carrega a chave. Separado para o teste poder medi-lo. */
export function comIdempotencia(chave) {
  return { headers: { "Idempotency-Key": chave } };
}

let isRefreshing = false;
let refreshQueue = [];

/** Uma entrada `{campo, mensagem}` do 422 → linha legível para o usuário. */
const _linhaDeCampo = (e) => {
  if (e?.mensagem) return e.campo ? `${e.campo}: ${e.mensagem}` : e.mensagem;
  // Rede da janela de deploy: bundle novo contra API antiga (array cru do
  // pydantic). Não é o formato vigente — só não pode virar "[object Object]".
  return e?.msg || e?.message || (typeof e === "string" ? e : JSON.stringify(e));
};

/**
 * `detail` CRU da API → uma linha legível. RÉGUA ÚNICA do front:
 * o interceptor abaixo, `handleApiError` e quem lê um corpo de erro que o
 * interceptor não alcança (download com `responseType: "blob"` —
 * `CompanyFechamento.js`) passam todos por aqui.
 *
 * O `detail` é POLIMÓRFICO no fio: lista de `{campo, mensagem}` no 422 de
 * validação de schema, string em todo o resto (inclusive nos 422 de regra de
 * negócio da antecipação). Devolve "" quando não há nada usável — o teto do
 * fallback é do chamador.
 */
export const flattenDetail = (detail) => {
  if (Array.isArray(detail)) return detail.map(_linhaDeCampo).join("; ");
  return typeof detail === "string" ? detail : "";
};

/**
 * Achata o `detail` estruturado do 422 numa string, PRESERVANDO a lista em
 * `detail_fields`. Idempotente e defensiva: só age quando `detail` é array.
 */
const _normalizeStructuredDetail = (response) => {
  const detail = response?.data?.detail;
  if (!Array.isArray(detail)) return;
  response.data.detail_fields = detail;
  response.data.detail = flattenDetail(detail);
};

const sendLogoutDiagnostic = (reason, errorStatus, errorMsg) => {
  try {
    const data = {
      reason, error_status: errorStatus, error_msg: errorMsg,
      path: window.location.pathname,
      had_token: !!localStorage.getItem("token"),
      had_refresh: !!localStorage.getItem("refresh_token"),
      timestamp: new Date().toISOString(),
    };
    navigator.sendBeacon(`${BACKEND_URL}/api/auth/logout-diagnostic`, JSON.stringify(data));
  } catch { /* best effort */ }
};

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const original = error.config;

    // Network error (server down, timeout, DNS) — DON'T logout, just reject
    if (!error.response) {
      return Promise.reject(error);
    }

    // 422 estruturado (A-R3d): a API devolve `detail` como LISTA de
    // `{campo, mensagem}` (contrato público — ver server.py e a ApiDocsPage).
    // Só ~1/3 das telas passa por `handleApiError`; as outras ~25 leem
    // `err.response.data.detail` CRU e o jogam num toast/estado. Sem esta
    // normalização, uma lista chegando lá vira "[object Object]" na tela —
    // uma REGRESSÃO em relação ao "Dados inválidos" que elas mostravam antes.
    //
    // Régua ÚNICA do front, aplicada num ponto só: `detail` vira a string
    // legível "campo: mensagem" (múltiplos juntados com "; "), e a lista
    // original fica preservada em `detail_fields` para quem quiser destacar
    // campo a campo. Divergência deliberada e local: no FIO `detail` é lista,
    // dentro do front é string. Nenhum outro status é tocado.
    _normalizeStructuredDetail(error.response);

    if (error.response.status === 401 && !original._retry && !original.url?.includes("/auth/")) {
      original._retry = true;
      if (!isRefreshing) {
        isRefreshing = true;
        // Use Cognito refresh token if auth_provider is cognito
        const authProvider = localStorage.getItem("auth_provider");
        const refreshToken = authProvider === "cognito"
          ? localStorage.getItem("cognito_refresh_token") || localStorage.getItem("refresh_token")
          : localStorage.getItem("refresh_token");
        if (refreshToken) {
          try {
            const res = await axios.post(`${BACKEND_URL}/api/auth/refresh`, {}, {
              headers: { Authorization: `Bearer ${refreshToken}` },
              withCredentials: true,
            });
            const newToken = res.data.cognito_token || res.data.token;
            localStorage.setItem("token", newToken);
            if (res.data.refresh_token) localStorage.setItem("refresh_token", res.data.refresh_token);
            if (res.data.cognito_token) {
              localStorage.setItem("auth_provider", "cognito");
            }
            isRefreshing = false;
            refreshQueue.forEach(cb => cb(newToken));
            refreshQueue = [];
            original.headers.Authorization = `Bearer ${newToken}`;
            return api(original);
          } catch (refreshError) {
            isRefreshing = false;
            refreshQueue.forEach(cb => cb(null));
            refreshQueue = [];
            // Only logout if refresh got explicit 401 (token truly invalid)
            // Don't logout on network errors (server restart, temporary downtime)
            if (refreshError.response?.status === 401) {
              sendLogoutDiagnostic("refresh_returned_401", 401, "Refresh token invalid");
              localStorage.removeItem("token");
              localStorage.removeItem("refresh_token");
              localStorage.removeItem("auth_provider");
              localStorage.removeItem("cognito_refresh_token");
              if (window.location.pathname !== "/login") window.location.href = "/login";
            }
            return Promise.reject(error);
          }
        } else {
          // No localStorage refresh token — try cookie-based refresh
          try {
            const res = await axios.post(`${BACKEND_URL}/api/auth/refresh`, {}, { withCredentials: true });
            const newToken = res.data.cognito_token || res.data.token;
            if (newToken) {
              localStorage.setItem("token", newToken);
              if (res.data.refresh_token) localStorage.setItem("refresh_token", res.data.refresh_token);
              isRefreshing = false;
              refreshQueue.forEach(cb => cb(newToken));
              refreshQueue = [];
              original.headers.Authorization = `Bearer ${newToken}`;
              return api(original);
            }
          } catch { /* cookie refresh failed too */ }
          isRefreshing = false;
          sendLogoutDiagnostic("no_refresh_token", null, "localStorage empty, cookie failed");
          localStorage.removeItem("token");
          if (window.location.pathname !== "/login") window.location.href = "/login";
          return Promise.reject(error);
        }
      } else {
        return new Promise((resolve, reject) => {
          refreshQueue.push((newToken) => {
            if (newToken) {
              original.headers.Authorization = `Bearer ${newToken}`;
              resolve(api(original));
            } else {
              reject(error);
            }
          });
        });
      }
    }
    return Promise.reject(error);
  }
);

export default api;

export const formatCurrency = (value) =>
  new Intl.NumberFormat("pt-BR", { style: "currency", currency: "BRL" }).format(value || 0);

export const formatDate = (dateStr) => {
  if (!dateStr) return "-";
  // Pure date string (YYYY-MM-DD): avoid UTC interpretation that shifts the day in BR timezone
  if (typeof dateStr === "string" && /^\d{4}-\d{2}-\d{2}$/.test(dateStr)) {
    const [y, m, d] = dateStr.split("-");
    return `${d}/${m}/${y}`;
  }
  return new Date(_ensureTimezone(dateStr)).toLocaleDateString("pt-BR", { timeZone: "America/Sao_Paulo" });
};

export const formatDateTime = (dateStr) => {
  if (!dateStr) return "-";
  return new Date(_ensureTimezone(dateStr)).toLocaleDateString("pt-BR", {
    timeZone: "America/Sao_Paulo",
    day: "2-digit", month: "2-digit", year: "numeric",
    hour: "2-digit", minute: "2-digit",
  });
};

/**
 * Ensure an ISO datetime string has timezone info. Legacy DB values stored
 * before 2026-05-07 may lack +00:00 — treat them as UTC so the frontend
 * correctly converts to America/Sao_Paulo.
 */
const _ensureTimezone = (dateStr) => {
  if (typeof dateStr !== "string") return dateStr;
  // Already has timezone (Z, +XX:XX, -XX:XX at end)
  if (/[Z]$/.test(dateStr) || /[+-]\d{2}:\d{2}$/.test(dateStr)) return dateStr;
  // Naive ISO string → assume UTC
  return dateStr + "+00:00";
};

/**
 * Offset FIXO da janela de negócio BR (GMT−3), espelho de `config.BR_TZ` do
 * backend. Fonte única de "que dia/mês é hoje no Brasil" em todo o front — ver a
 * justificativa completa em `brCompetencyOf` logo abaixo.
 */
const BR_OFFSET_MS = 3 * 60 * 60 * 1000;

/**
 * "Hoje" e "mês" na janela de negócio BR (GMT−3), independentemente do fuso do
 * navegador. Devolve YYYY-MM-DD / YYYY-MM. Espelha `config.br_today_str()` /
 * `br_month_str()` do backend.
 *
 * Usa o MESMO offset fixo de `brCompetencyOf` — não IANA/`America/Sao_Paulo`.
 * Com o tz database, um dia de horário de verão (BRT = UTC−2) cairia numa data
 * aqui e em outra no Python, e a divergência apareceria justamente na borda do
 * dia/mês, que é onde ela custa dinheiro.
 */
export const brTodayStr = () => {
  const br = new Date(Date.now() - BR_OFFSET_MS);
  return `${br.getUTCFullYear()}-${String(br.getUTCMonth() + 1).padStart(2, "0")}-${String(br.getUTCDate()).padStart(2, "0")}`;
};

export const brMonthStr = () => brTodayStr().slice(0, 7);

/**
 * Competência BR ("YYYY-MM") de um timestamp armazenado em UTC. Espelha o
 * backend `config.br_competency_of()`. Fatiar `created_at.substring(0, 7)`
 * responde a pergunta errada: 01:00Z do dia 1º nasceu no mês ANTERIOR em
 * Brasília. Devolve "" quando o valor está ausente ou é ilegível.
 *
 * Usa OFFSET FIXO −3, espelho de `config.BR_TZ` do backend — NÃO usar
 * IANA/`America/Sao_Paulo` para não criar segunda fonte de verdade. O tz
 * database carrega as regras históricas e futuras de horário de verão: com ele
 * um timestamp em época de DST (BRT = UTC−2, até 2019) cai numa competência e
 * o Python cai em outra, e o dia em que o Brasil reinstituir o horário de verão
 * as duas réguas divergem em silêncio na borda do mês.
 */
export const brCompetencyOf = (dateStr) => {
  if (!dateStr) return "";
  const d = new Date(_ensureTimezone(dateStr));
  if (Number.isNaN(d.getTime())) return "";
  const br = new Date(d.getTime() - BR_OFFSET_MS);
  return `${br.getUTCFullYear()}-${String(br.getUTCMonth() + 1).padStart(2, "0")}`;
};

/**
 * Shifts a YYYY-MM-DD date string by N months in BR calendar time (not
 * affected by browser TZ). Positive or negative offset.
 */
export const brShiftMonth = (dateStr, offset) => {
  const [y, m, d] = dateStr.split("-").map(Number);
  const target = new Date(Date.UTC(y, m - 1 + offset, d));
  return target.toISOString().slice(0, 10);
};

export const formatCPF = (value) => {
  if (!value) return "";
  const digits = value.replace(/\D/g, "");
  if (digits.length <= 11) {
    return digits.replace(/(\d{3})(\d{3})(\d{3})(\d{0,2})/, (_, a, b, c, d) =>
      d ? `${a}.${b}.${c}-${d}` : c ? `${a}.${b}.${c}` : b ? `${a}.${b}` : a
    );
  }
  return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2})/, (_, a, b, c, d, e) =>
    e ? `${a}.${b}.${c}/${d}-${e}` : d ? `${a}.${b}.${c}/${d}` : c ? `${a}.${b}.${c}` : b ? `${a}.${b}` : a
  );
};

export const formatCNPJ = (value) => {
  if (!value) return "";
  const digits = value.replace(/\D/g, "").slice(0, 14);
  return digits.replace(/(\d{2})(\d{3})(\d{3})(\d{4})(\d{0,2})/, (_, a, b, c, d, e) =>
    e ? `${a}.${b}.${c}/${d}-${e}` : d ? `${a}.${b}.${c}/${d}` : c ? `${a}.${b}.${c}` : b ? `${a}.${b}` : a
  );
};

export const formatPhone = (value) => {
  if (!value) return "";
  const digits = value.replace(/\D/g, "").slice(0, 11);
  if (digits.length <= 10) {
    return digits.replace(/(\d{2})(\d{4})(\d{0,4})/, (_, a, b, c) =>
      c ? `(${a}) ${b}-${c}` : b ? `(${a}) ${b}` : a ? `(${a}` : ""
    );
  }
  return digits.replace(/(\d{2})(\d{5})(\d{0,4})/, (_, a, b, c) =>
    c ? `(${a}) ${b}-${c}` : b ? `(${a}) ${b}` : a ? `(${a}` : ""
  );
};

export const formatCEP = (value) => {
  if (!value) return "";
  const digits = value.replace(/\D/g, "").slice(0, 8);
  return digits.replace(/(\d{5})(\d{0,3})/, (_, a, b) => b ? `${a}-${b}` : a);
};

export const formatCPFCNPJ = (value) => {
  if (!value) return "-";
  const digits = value.replace(/\D/g, "");
  if (digits.length <= 11) return formatCPF(value);
  return formatCNPJ(value);
};

export const formatPercent = (value) => {
  if (value === null || value === undefined) return "-";
  return `${Number(value).toFixed(0)}%`;
};


/**
 * Erro do axios → uma string para o usuário.
 *
 * O 422 já chega achatado pelo interceptor (`_normalizeStructuredDetail`),
 * então aqui `detail` é string em todos os status. `flattenDetail` cobre de
 * graça quem chame esta função com um erro que NÃO passou pelo interceptor
 * (axios cru, teste, mock) — mesma régua "campo: mensagem".
 * Quem precisa dos campos separados lê `err.response.data.detail_fields`.
 */
export const handleApiError = (err, fallback = "Erro") => {
  const status = err?.response?.status;
  const detail = err?.response?.data?.detail;
  if (status === 403 && typeof detail === "string" && detail.includes("2FA")) {
    return "Ative a autenticação de dois fatores (2FA) no Dashboard para acessar a plataforma.";
  }
  return flattenDetail(detail) || fallback;
};
